import { useEffect, useMemo, useState } from 'react';
import type { LeadCategory } from '@schwarz/contract';

/** The categories the filter offers — see the note on the state below. */
type LeadCategoryFilter = Exclude<LeadCategory, 'UNKNOWN'>;
import { LEAD_CATEGORY_FILTERS, leadCategoryLabels } from '@schwarz/contract';

import { useGetQueueQuery } from '../../store/contacts-api.js';
import { useGetInteractionsQuery } from '../../store/interactions-api.js';
import { messageOf } from '../../store/error-message.js';
import { IconDownload, IconInteractions, IconSearch } from '../../ui/icons.js';
import { initials } from '../../ui/initials.js';
import { PAGE_SIZES, Pagination } from '../../ui/Pagination.js';
import { when } from '../../ui/when.js';
import { CategoryChip } from './CategoryChip.js';
import { InteractionDrawer } from './InteractionDrawer.js';
import { barGeometry, formatScore } from './sentiment.js';
import styles from './InteractionsPage.module.css';

/** How long to wait before turning keystrokes into a request. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * How often to look for newly analysed calls.
 *
 * ## Why this page polls at all now
 *
 * It did not, and the reasoning was half right: nothing about an interaction
 * changes once it exists, so there is nothing to re-read. But rows *appear*, and
 * they appear late — an analysis lands roughly seven seconds after the call it
 * describes resolves, by which time there is no call in flight to key polling on.
 * So an operator who finished a call and came here to read the result sat looking
 * at a page that would not have updated until they navigated away and back.
 *
 * Faster while a call is live, because one is about to produce a row; slower
 * otherwise, but never off — the slow interval is what catches the analysis that
 * arrives after the call has already left the in-flight count.
 */
const ANALYSIS_POLL_MS = 3000;
const IDLE_POLL_MS = 12_000;

/**
 * Interactions (S08).
 *
 * ## Why this page is not a list of calls
 *
 * An interaction is a call that has been *analysed*. A call nobody answered has
 * nothing to analyse and a failed call has no transcript, so neither appears
 * here — they belong to the contact's own history, where the question is whether
 * we reached them rather than what they said. Every row on this page carries a
 * conclusion an operator can act on.
 *
 * ## Why filtering happens on the server
 *
 * The mock filters an array it already holds. Here the set can run to every call
 * ever analysed, so search, category and paging are all query parameters —
 * which also makes the export in S09 exact, because "the current filtered set"
 * is a filter the server can be handed rather than a list the browser happens
 * to be showing.
 */
export function InteractionsPage(): JSX.Element {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Typed to the four the filter offers, not to LeadCategory: UNKNOWN means
  // the analyzer could not decide, and the server rejects it as a filter.
  const [category, setCategory] = useState<LeadCategoryFilter | ''>('');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  // Debounced so typing does not put a request behind every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any change to what is being asked for starts again at the first page:
  // holding page 4 while narrowing to three results shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [search, category, pageSize]);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(category ? { category } : {}),
    }),
    [page, pageSize, search, category],
  );

  // The queue read is shared with the contacts page, so on a console where both
  // have been visited this costs nothing extra — and it is the only thing here
  // that knows whether a call is live.
  const { data: queue } = useGetQueueQuery(undefined, { pollingInterval: IDLE_POLL_MS });
  const busy = (queue?.callsInFlight ?? 0) > 0;

  const { data, isLoading, isError, error, refetch } = useGetInteractionsQuery(query, {
    pollingInterval: busy ? ANALYSIS_POLL_MS : IDLE_POLL_MS,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The page can outrun the data when rows disappear beneath it — an analysis
  // corrected, a contact deleted. Clamp rather than show an empty table.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const filtering = search.trim() !== '' || category !== '';

  /**
   * The export link, carrying the same filter the table is showing.
   *
   * An anchor rather than a fetch: the request is same-origin so the session
   * cookie goes with it, and letting the browser take the file avoids holding a
   * megabyte of spreadsheet in a string only to hand it straight back. Paging is
   * left off deliberately — the export is the whole filtered set, not the page.
   */
  const exportHref = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (category) params.set('category', category);
    const query = params.toString();
    return `/api/interactions/export${query ? `?${query}` : ''}`;
  }, [search, category]);

  return (
    <section className={styles.view}>
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <IconSearch />
          <input
            type="search"
            placeholder="Search contact, company, summary or category…"
            value={searchInput}
            aria-label="Search interactions"
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>

        <select
          className={styles.select}
          value={category}
          aria-label="Filter by lead category"
          onChange={(event) => setCategory(event.target.value as LeadCategoryFilter | '')}
        >
          <option value="">All categories</option>
          {LEAD_CATEGORY_FILTERS.map((value) => (
            <option key={value} value={value}>
              {leadCategoryLabels[value]}
            </option>
          ))}
        </select>

        {/* Disabled with nothing to export rather than hidden: the affordance
            should stay where the operator expects it. */}
        <a
          className={`${styles.export} ${total === 0 ? styles.exportOff : ''}`}
          href={total === 0 ? undefined : exportHref}
          aria-disabled={total === 0}
          title={
            total === 0
              ? 'Nothing to export'
              : `Download ${String(total)} interaction${total === 1 ? '' : 's'} as CSV`
          }
        >
          <IconDownload />
          Export
        </a>
      </div>

      <div className={styles.panel}>
        {isLoading && <div className={styles.state}>Loading interactions…</div>}

        {isError && (
          <div className={styles.state}>
            <p>{messageOf(error)}</p>
            <button type="button" className={styles.retry} onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && total === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <IconInteractions />
            </div>
            {filtering ? (
              <>
                <h3>Nothing matches</h3>
                <p>No analysed call matches that search or category.</p>
              </>
            ) : (
              <>
                <h3>No interactions yet</h3>
                {/* Deliberately explicit about why the page is empty: an
                    operator who has placed calls and sees nothing here should
                    learn that analysis is the missing step, not assume the calls
                    were lost. */}
                <p>
                  A call appears here once it has been answered and analysed. Calls that were not
                  answered stay in the contact&rsquo;s own history.
                </p>
              </>
            )}
          </div>
        )}

        {!isLoading && !isError && total > 0 && (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Contact</th>
                    <th scope="col">Category</th>
                    <th scope="col">Sentiment</th>
                    <th scope="col">Summary</th>
                    <th scope="col">Call time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const bar = barGeometry(row.sentiment.score);
                    return (
                      <tr
                        key={row.id}
                        className={styles.row}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open the interaction with ${row.contact.name}`}
                        onClick={() => setOpenId(row.id)}
                        onKeyDown={(event) => {
                          // A row that opens on click must open from the
                          // keyboard too, or the drawer is unreachable without a
                          // mouse.
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setOpenId(row.id);
                          }
                        }}
                      >
                        <td>
                          <div className={styles.who}>
                            <div className={styles.av} aria-hidden="true">
                              {initials(row.contact.name)}
                            </div>
                            <div>
                              <b>{row.contact.name}</b>
                              <small>{row.contact.company ?? '—'}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <CategoryChip category={row.leadCategory} />
                        </td>
                        <td>
                          <div className={styles.senti}>
                            <div className={styles.track} aria-hidden="true">
                              <span className={styles.centre} />
                              <span
                                className={`${styles.fill} ${styles[bar.tone]}`}
                                style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                              />
                            </div>
                            <small className={styles[bar.tone]}>
                              {formatScore(row.sentiment.score)}
                            </small>
                          </div>
                        </td>
                        <td className={styles.summary}>{row.summary ?? '—'}</td>
                        <td className={styles.when}>{when(row.callAt ?? row.analyzedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPage={setPage}
              onPageSize={setPageSize}
            />
          </>
        )}
      </div>

      <InteractionDrawer interactionId={openId} onClose={() => setOpenId(null)} />
    </section>
  );
}
